import { useEffect, useState, useCallback } from 'react';
import {
  Mail, Calendar, CheckCircle2, XCircle, Loader2, ExternalLink,
  RefreshCw, AlertTriangle, Link2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { apiFetch } from '../../fetchInterceptor';
import { useWorkspace } from '../../context/WorkspaceContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GoogleIntegrationCard
 * Settings → Integrations card for the Gmail + Calendar add-on.
 * Separate from the app's sign-in flow (which uses Emergent-managed Google).
 */
export default function GoogleIntegrationCard() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/integrations/google/status?workspace_id=${wsId}`,
      );
      if (r.ok) setStatus(await r.json());
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  // Listen for completion message from the OAuth popup window.
  useEffect(() => {
    const handler = (ev) => {
      if (!ev?.data) return;
      if (ev.data.type === 'google_oauth_complete') {
        if (ev.data.ok) toast.success('Google connected.');
        else toast.error('Google connection failed.');
        load();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [load]);

  // If the user lands back here via redirect, refresh once.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('google_connected')) {
      load();
      url.searchParams.delete('google_connected');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    if (!wsId) return;
    setConnecting(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/integrations/google/connect?workspace_id=${wsId}`,
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(body.detail || 'Could not start Google connect.');
        return;
      }
      // Open a popup so the user doesn't lose their Settings page state.
      const popup = window.open(
        body.auth_url, 'google_oauth',
        'width=520,height=640,menubar=no,toolbar=no,location=no,status=no',
      );
      if (!popup) {
        // Popup blocked — fall back to a top-level navigation.
        window.location.assign(body.auth_url);
      }
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!wsId) return;
    if (!window.confirm('Disconnect Google for this workspace? Gmail auto-logging and calendar widgets will stop working.')) {
      return;
    }
    setDisconnecting(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/integrations/google/disconnect?workspace_id=${wsId}`,
        { method: 'DELETE' },
      );
      if (r.ok) {
        toast.success('Google disconnected.');
        load();
      } else {
        toast.error('Could not disconnect.');
      }
    } finally {
      setDisconnecting(false);
    }
  };

  const syncContacts = async () => {
    if (!wsId) return;
    setSyncing(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/integrations/google/gmail/sync-contacts?workspace_id=${wsId}&lookback_days=14`,
        { method: 'POST' },
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(body.detail || 'Sync failed.');
        return;
      }
      if (body.error === 'no_contacts') {
        toast.warning('Add at least one contact first.');
      } else {
        toast.success(
          `Scanned ${body.scanned} emails · ${body.matched} matched · ${body.new_logged} newly logged.`,
        );
      }
    } finally {
      setSyncing(false);
    }
  };

  const isConfigured = status?.configured;
  const isConnected = status?.connected;

  return (
    <Card data-testid="google-integration-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-rose-500 via-amber-500 to-emerald-500 flex items-center justify-center text-white font-bold">
              G
            </div>
            <div>
              <CardTitle>Google (Gmail + Calendar)</CardTitle>
              <CardDescription>
                Auto-log client emails to CRM, draft replies with AI, and show today&apos;s meetings on your dashboard.
              </CardDescription>
            </div>
          </div>
          {loading ? (
            <Badge variant="secondary"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Checking…</Badge>
          ) : !isConfigured ? (
            <Badge variant="secondary"><AlertTriangle className="w-3 h-3 mr-1" />Setup required</Badge>
          ) : isConnected ? (
            <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" data-testid="google-status-connected">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
            </Badge>
          ) : (
            <Badge variant="secondary" data-testid="google-status-not-connected">
              <XCircle className="w-3 h-3 mr-1" /> Not Connected
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!isConfigured && (
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 text-xs text-amber-900 dark:text-amber-200">
            Server-side OAuth credentials missing. Ask your admin to set
            <code className="mx-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 font-mono">GOOGLE_OAUTH_CLIENT_ID</code>
            and
            <code className="mx-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 font-mono">GOOGLE_OAUTH_CLIENT_SECRET</code>
            in the backend environment.
          </div>
        )}

        {isConfigured && isConnected && (
          <div className="p-4 rounded-lg bg-muted/50 space-y-2 text-sm" data-testid="google-connection-info">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Connected as</span>
              <span className="font-medium" data-testid="google-account-email">{status.google_email || '—'}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Connected at</span>
              <span>{status.connected_at?.slice(0, 19).replace('T', ' ')}</span>
            </div>
            <div className="flex flex-wrap gap-1 pt-2">
              {(status.scopes || []).filter((s) => s.includes('gmail') || s.includes('calendar')).map((s) => (
                <Badge key={s} variant="outline" className="text-[10px]">
                  {s.includes('gmail.readonly')   && <Mail     className="w-2.5 h-2.5 mr-1" />}
                  {s.includes('gmail.compose')    && <Mail     className="w-2.5 h-2.5 mr-1" />}
                  {s.includes('calendar.readonly')&& <Calendar className="w-2.5 h-2.5 mr-1" />}
                  {s.split('/').pop()}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {isConfigured && !isConnected && (
            <Button
              onClick={connect}
              disabled={connecting}
              className="bg-gradient-to-r from-rose-500 via-amber-500 to-emerald-500 hover:opacity-90 text-white"
              data-testid="google-connect-btn"
            >
              {connecting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening Google…</>
              ) : (
                <><Link2 className="w-4 h-4 mr-2" /> Connect Google</>
              )}
            </Button>
          )}
          {isConfigured && isConnected && (
            <>
              <Button
                variant="outline" onClick={syncContacts} disabled={syncing}
                data-testid="google-sync-contacts-btn"
              >
                {syncing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Syncing…</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" /> Sync emails → Contacts</>
                )}
              </Button>
              <Button
                variant="ghost" onClick={disconnect} disabled={disconnecting}
                data-testid="google-disconnect-btn"
                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
              >
                {disconnecting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Disconnecting…</>
                ) : (
                  'Disconnect'
                )}
              </Button>
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground self-center ml-1"
              >
                <ExternalLink className="w-3 h-3" /> Revoke on Google
              </a>
            </>
          )}
        </div>

        <ul className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
          <li>· <strong>Gmail:</strong> auto-logs inbound client emails to the matching Contact timeline.</li>
          <li>· <strong>AI Buck:</strong> can draft replies in your Gmail inbox from the AI Hub.</li>
          <li>· <strong>Calendar:</strong> today&apos;s meetings appear on the Command Center and (optionally) inside your Brief.</li>
          <li>· Tokens are encrypted at rest. Disconnect any time.</li>
        </ul>
      </CardContent>
    </Card>
  );
}
